import axios, { AxiosError, type AxiosRequestConfig, CanceledError } from 'axios';

import { purgeClientSession, registerAxiosEphemeralResetForSessionPurge } from '@/shared/auth/purgeClientSession';
import { keysToCamel, keysToSnake } from '@/shared/utils/caseConverter';

const noTokenPatterns = ['/api/auth/login', '/api/auth/refresh'];

/** Bearer 미첨부 + 401 시 토큰 갱신 생략 (비로그인 공개 API — 만료 토큰·갱신 실패로 로그인 페이지로 보내지 않기 위함) */
const publicApiUrlPatterns = ['/api/public/'];

const DEFAULT_THROTTLE_MS = 200;
const OTHER_THROTTLE_MS = 1000;
const lastCallMap = new Map<string, number>();

declare module 'axios' {
  export interface AxiosRequestConfig {
    isThrottle?: boolean;
    snakeCaseParams?: boolean;
    emptyStringToNull?: boolean;
  }
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}
function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function resetAxiosEphemeralState() {
  lastCallMap.clear();
  refreshSubscribers = [];
  isRefreshing = false;
}

registerAxiosEphemeralResetForSessionPurge(resetAxiosEphemeralState);

axiosInstance.interceptors.request.use((config) => {
  const url = config.url ?? '';
  const skipBearer = noTokenPatterns.some((pattern) => url.includes(pattern)) || publicApiUrlPatterns.some((pattern) => url.includes(pattern));

  if (!skipBearer) {
    const token = window.localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }

  const isFormData = config.data instanceof FormData;

  if (config.data && !isFormData) config.data = keysToSnake(config.data);
  if (config.params && config.snakeCaseParams) config.params = keysToSnake(config.params);

  if (config.isThrottle) {
    const method = (config.method ?? 'get').toLowerCase();
    const isGet = method === 'get';
    const jsonParams = JSON.stringify(isGet ? config.params : config.data);
    const throttleMs = isGet ? DEFAULT_THROTTLE_MS : OTHER_THROTTLE_MS;
    const key = `${method}:${config.url}-${jsonParams}`;
    const now = Date.now();
    const last = lastCallMap.get(key) || 0;
    if (now - last < throttleMs) {
      return Promise.reject(new CanceledError(`Throttled: ${key}`));
    }
    lastCallMap.set(key, now);
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    const binaryTypes = ['blob', 'arraybuffer', 'stream'];
    if (binaryTypes.includes(response.config.responseType || '')) {
      return response;
    }
    response.data = keysToCamel(response.data);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const is401 = error.response?.status === 401;

    const skipRefresh =
      noTokenPatterns.some((pattern) => originalRequest.url?.includes(pattern)) ||
      publicApiUrlPatterns.some((pattern) => originalRequest.url?.includes(pattern));

    if (!is401 || !originalRequest || originalRequest._retry || skipRefresh) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token: string) => {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    isRefreshing = true;

    try {
      // 인터셉터 재귀 방지를 위해 순수 axios로 직접 호출
      const refreshRes = await axios.post(`${import.meta.env.VITE_API_URL}/api/auth/refresh`, undefined, { withCredentials: true });

      const refreshData = keysToCamel(refreshRes.data) as { success: boolean; data: { accessToken: string } | null };
      const newAccessToken = refreshData.data?.accessToken;
      if (!newAccessToken) throw new Error('토큰 갱신 실패');

      window.localStorage.setItem('accessToken', newAccessToken);
      onRefreshed(newAccessToken);

      if (originalRequest.headers) {
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
      }
      return axiosInstance(originalRequest);
    } catch {
      purgeClientSession();
      window.location.href = '/auth/signin';
      // 리다이렉트 후 에러가 UI로 전파되지 않도록 pending 상태 유지
      return new Promise(() => {});
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
